<script setup>
import { t } from "@/controllerLK/AvalLocalization";
import MapUploadPanel from "@/components/MapPage/MapUploadPanel.vue";
import { useMapUpload } from "@/composables/useMapUpload";
import UploadIcon from "@/components/Icons/UploadIcon.vue";
import {defineEmits, defineProps} from "vue";
import {useNotificationsStore} from "@/store/notifications";

const props = defineProps({
  createdMapList: {
    type: Array,
    required: true,
    default: () => [],
  }
});

const emit = defineEmits([
  "update:model-value",
  "set-map-loading",
  "map-created",
  "toggle-create-map-modal",
  "map-creation-error"
]);

const notificationStore = useNotificationsStore();

// Используем composable для логики работы с картами
const { mapList, mapSearchString, searchMap, createMap, uploadMap } = useMapUpload();

// Закрытие модального окна
const closeModal = () => {
  emit("toggle-create-map-modal");
};

// Обработка создания карты
const handleCreateMap = async (map) => {
  // Проверка, что карта с данным названием не была загружена ранее.
  if (props.createdMapList.some(createdMap => createdMap.name === map.name)) {
    notificationStore.createNotify(t("no_map_view.toast.already_exists"), "warning");
    return;
  }

  await createMap(map, {
    onStart: () => {
      emit("set-map-loading", true);
      closeModal();
    },
    onSuccess: (mapData) => {
      emit("set-map-loading", false);
      emit("map-created", mapData);
    },
    onError: (error) => {
      emit("set-map-loading", false);
      emit("map-creation-error", error);
    }
  });
};

// Обработка загрузки файла
const handleFileUpload = async (e) => {
  const files = e.target?.files;
  if (files && files.length > 0) {
    await uploadMap(files[0], {
      onStart: () => {
        emit("set-map-loading", true);
        closeModal();
      },
      onSuccess: (mapData) => {
        emit("set-map-loading", false);
        emit("map-created", mapData);
      },
      onError: (error) => {
        emit("set-map-loading", false);
        emit("map-creation-error", error);
      }
    });
    // Сброс значения input для возможности повторной загрузки того же файла
    e.target.value = "";
  }
};

// Обработчик клика на backdrop
const handleBackdropClick = (e) => {
  if (e.target.classList.contains("modal-backdrop")) {
    closeModal();
  }
};
</script>

<template>
  <div
    class="modal-backdrop"
    @click="handleBackdropClick"
  >
    <div class="modal">
      <div class="modal__header">
        <h3 class="modal__title">
          {{ t("no_map_view.add_region") }}
        </h3>
        <button
          class="modal__close"
          @click="closeModal"
        >
          <IconCross />
        </button>
      </div>

      <span class="modal__subtitle">
        {{ t("no_map_view.subtitle") }}
        <br>
        {{ t("no_map_view.subtitle_format") }}
      </span>

      <MapUploadPanel
        :model-value="mapSearchString"
        :items="mapList"
        :placeholder="t('no_map_view.placeholder')"
        :with-file-upload="true"
        :flat="true"
        @update:model-value="searchMap"
        @add-item="handleCreateMap"
        @file-selected="handleFileUpload"
      >
        <template #action-button="{ onClick }">
          <ButtonAccentSecondary
            size="large"
            @click="onClick"
          >
            {{ t("no_map_view.upload_map") }}
            <UploadIcon />
          </ButtonAccentSecondary>
        </template>
      </MapUploadPanel>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  position: relative;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 576px;
  box-shadow: 1px 2px 10px 0 var(--GrayGray50);
  background-color: var(--background-bg-white);
  z-index: 1001;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: -15px;
  }

  &__title {
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;
    color: var(--text-text-main);
    margin: 0;
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 400;
    color: var(--text-text-gray);
    line-height: 140%;
  }

  &__close {
    background: none;
    border: none;
    font-size: 28px;
    line-height: 1;
    color: var(--icon-icon-secondary);
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: var(--icon-icon-on);
    }
  }
}
</style>